import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./FormCreateQuestion.scss";
import Button from "../../Button/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFormState, useWatch } from "react-hook-form";
import FormSelect, { IOption } from "../FormInput/FormSelect/FormSelect";
import CustomCheckbox from "../FormInput/CustomCheckbox/CustomCheckbox";
import CustomRadio from "../FormInput/CustomRadio/CustomRadio";
import {
  translations,
  QuestionPayload,
  LANGUAGE_TRANSLATION,
  QUESTION_ACTOR,
  ANSWER_TYPE,
  initialQuestion,
} from "../../../models/SurveyModels";
import CustomTextArea from "../FormInput/CustomTextArea/CustomTextArea";
import Skeleton from "react-loading-skeleton";
import Translations, { IPopulated } from "../Translations";

interface IFormQuestions {
  isLoading?: boolean;
  isInModal?: boolean;
  questionData?: QuestionPayload;
  categories: IOption[];
  answerType: IOption[];
  onSumbitForm: (data: QuestionPayload) => void;
  setOpenModal?: (value: boolean) => void;
}

const FormCreateQuestion = ({
  isInModal,
  isLoading,
  questionData,
  categories,
  answerType,
  onSumbitForm,
  setOpenModal,
}: IFormQuestions) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    clearErrors,
    unregister,
  } = useForm<QuestionPayload>({
    mode: "all",
  });
  const { id } = useParams();
  const [isChecked, setIsChecked] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const options = [
    {
      label: `${t("createQuestion.questionData.domain.domains.HR")}`,
      value: QUESTION_ACTOR.HR,
    },
    {
      label: `${t("createQuestion.questionData.domain.domains.Manager")}`,
      value: QUESTION_ACTOR.MANAGER,
    },
    {
      label: `${t("createQuestion.questionData.domain.domains.None")}`,
      value: "none",
    },
  ];
  const [question, setQuestion] = useState<IPopulated>(
    questionData || initialQuestion
  );

  const [translationsForIndex, setTranslations] = useState<IOption[][]>([
    translations,
  ]);
  const [selectedTranslations, setSelectedTranslations] = useState<string[]>(
    []
  );
  const handleAddLanguage = () => {
    const newTranslation = {
      text: "",
      language: "",
    };

    setValue(
      `translations.${controls.translations?.length || 0}`,
      newTranslation
    );

    setQuestion((oldValue) => ({
      ...oldValue!,
      translations: [...(oldValue?.translations || []), newTranslation],
    }));
    setTranslations((prevTranslations) => [
      ...prevTranslations,
      translations.filter((item) =>
        selectedTranslations.every((term) =>
          Object.values(item).some(
            (value) => !value.toLowerCase().includes(term.toLowerCase())
          )
        )
      ),
    ]);
  };
  const removeLanguage = (index: number) => {
    const newTranslations = [...question.translations];
    newTranslations.splice(index, 1);
    setQuestion((oldValue) => ({
      ...oldValue,
      translations: newTranslations,
    }));
    setSelectedTranslations((old) => [...old, ...old.splice(index, 1)]);
    setTranslations((old) => [...old, ...old.splice(index, 1)]);
    unregister(`translations.${index}`);
  };

  const formState = useFormState({
    control,
  });
  const [selectedValue, setSelectedValue] = useState<QUESTION_ACTOR | "none">(
    QUESTION_ACTOR.HR
  );
  const handleRadioChange = useCallback(
    (value: string) => {
      setSelectedValue(value as QUESTION_ACTOR | "none");
      value !== "none"
        ? setValue("actor", value as QUESTION_ACTOR)
        : unregister("actor");
    },
    [setValue, unregister]
  );

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      setValue("self", checked);
    },
    [setValue]
  );

  const controls = useWatch({
    control,
  });

  useEffect(() => {
    if (questionData) {
      setQuestion(questionData);
      questionData.answerMode &&
        setValue("answerMode", questionData.answerMode);
      handleRadioChange(questionData.actor || "none");
      handleCheckboxChange(questionData.self || false);
      if (questionData?.translations) {
        questionData?.translations.forEach(({ language, text }, index) => {
          setValue(`translations.${index}`, { language, text });
          /*         setValue(`translations.${index}.text`, text); */
          setSelectedTranslations((old) => {
            if (old.includes(language)) return [...old];
            else return [...old!, language];
          });
        });
        setTranslations((prevTranslations) => {
          const newTranslations = translations.filter((item) =>
            selectedTranslations.every((term) =>
              Object.values(item).some(
                (value) => !value.toLowerCase().includes(term.toLowerCase())
              )
            )
          );
          const isDuplicate = prevTranslations.some((prevItem) => {
            if (prevItem.length !== newTranslations.length) {
              return false;
            }
            return prevItem.every((prevObj, index) => {
              const newObj = newTranslations[index];
              return Object.entries(prevObj).every(
                ([key, value]) => newObj[key] === value
              );
            });
          });

          if (isDuplicate) {
            return prevTranslations;
          } else {
            return [...prevTranslations, newTranslations];
          }
        });
      }
      questionData?.categoryIds &&
        setValue(`categoryIds.${0}`, questionData?.categoryIds[0]);
      questionData?.type && setValue(`type`, questionData?.type);
    } else {
      const newTranslation = {
        text: "",
        language: "",
      };
      handleRadioChange(QUESTION_ACTOR.HR);
      setValue(`translations.${0}`, newTranslation);
    }
  }, [handleCheckboxChange, handleRadioChange, questionData, reset, setValue]);

  return (
    <form className="form-create-questions">
      <div className="navbar fixed mb-4">
        <div className="mb-4 container__top">
          <div className="left">
            <h1 className="title-page capitalize-first-letter">
              {t(`createQuestion.title.${id ? "edit" : "create"}`)}
            </h1>
            <p className="page-description capitalize-first-letter mt-1">
              {t(`createQuestion.description.${id ? "edit" : "create"}`)}
            </p>
          </div>
          {!isInModal && (
            <div className="right">
              <div style={{ marginRight: "1rem" }}>
                <Button
                  color="tertiary"
                  noBorder={true}
                  className="capitalize-first-letter"
                  onClick={() => navigate(-1)}
                >
                  <span>{t("createAgency.labelCancelButton.edit")}</span>
                </Button>
              </div>
              <Button
                onClick={handleSubmit(onSumbitForm)}
                className="capitalize-first-letter"
              >
                <span>{t("createQuestion.labelSubmitButton.create")}</span>
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="form-container">
        <div className="form-content">
          <div
            className={`${isInModal ? "col-lg-12" : "col-lg-8"} form-content `}
          >
            <div className="mb-4">
              <h2 className="registry">
                {t("createQuestion.questionData.domain.title")}
              </h2>
              <p className="page-description">
                {t("createQuestion.questionData.domain.text")}
              </p>
            </div>
            <div className="ms-3">
              <div className="my-4">
                {isLoading ? (
                  <Skeleton
                    count={3}
                    inline
                    height={30}
                    containerClassName="container-radio-skeleton"
                  />
                ) : (
                  <CustomRadio
                    options={options}
                    selectedValue={selectedValue}
                    {...register("actor")}
                    onChange={(value) => handleRadioChange(value as string)}
                  />
                )}
              </div>
              <div className="check-item mt-3 mb-4">
                {isLoading ? (
                  <Skeleton height={30} inline width="33%" />
                ) : (
                  <CustomCheckbox
                    label={t("createQuestion.questionData.domain.checkValue")}
                    {...register("self")}
                    onChange={handleCheckboxChange}
                    checked={isChecked}
                  />
                )}
              </div>
            </div>
            <div className="mt-4">
              <h2 className="registry mb-4">
                {t("createQuestion.questionData.fields.title")}
              </h2>
              <div className="row position-relative">
                {isLoading ? (
                  <Skeleton
                    count={2}
                    inline
                    height={44}
                    containerClassName="container-radio-skeleton"
                  />
                ) : (
                  <>
                    <FormSelect
                      typeErrors={
                        formState.errors?.categoryIds &&
                        (formState.errors?.categoryIds[0]?.message as string)
                      }
                      isInvalid={Boolean(
                        formState.errors?.categoryIds &&
                          formState.errors?.categoryIds[0]
                      )}
                      options={categories}
                      wrapperClass="col-lg-6"
                      defaultValue={
                        questionData?.categoryIds &&
                        questionData?.categoryIds[0] &&
                        questionData?.categoryIds[0]
                      }
                      required
                      {...register(`categoryIds.${0}`, {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      label={t(
                        "createQuestion.questionData.fields.category.label"
                      )}
                      placeholder={t(
                        "createQuestion.questionData.fields.category.placeholder"
                      )}
                      onChange={(selected) => {
                        clearErrors(`categoryIds.${0}`);
                        setValue(`categoryIds.${0}`, selected);
                      }}
                    />
                    <FormSelect
                      typeErrors={
                        formState.errors?.answerMode &&
                        (formState.errors?.answerMode?.message as string)
                      }
                      isInvalid={formState.errors.hasOwnProperty(`answerMode`)}
                      options={answerType}
                      wrapperClass="col-lg-6"
                      defaultValue={
                        questionData?.answerMode && questionData?.answerMode
                      }
                      label={
                        t(
                          "createQuestion.questionData.fields.responseMode.label"
                        ) + "*"
                      }
                      placeholder={t(
                        "createQuestion.questionData.fields.responseMode.placeholder"
                      )}
                      {...register(`answerMode`, {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      onChange={(value) => {
                        clearErrors(`answerMode`);
                        setValue(`answerMode`, value as ANSWER_TYPE);
                      }}
                    />
                  </>
                )}
                <div className="col-lg-12 form-content">
                  <Translations
                    translationsforIndex={translationsForIndex}
                    handleAddLanguage={handleAddLanguage}
                    removeLanguage={removeLanguage}
                    setSelectedTranslations={setSelectedTranslations}
                    isLoading={isLoading}
                    formState={formState}
                    populated={controls as IPopulated}
                    register={register}
                    unregister={unregister}
                    clearErrors={clearErrors}
                    setValue={setValue}
                  />
                  {/*    {question.translations &&
                    question.translations.map((item, index) => (
                      <Fragment key={index}>
                        <div className="form-group form-group-translations row">
                          {isLoading ? (
                            <Skeleton inline width="50%" height={44} />
                          ) : (
                            <>
                              <FormSelect
                                options={
                                  translationsForIndex[index] || translations
                                }
                                required
                                typeErrors={
                                  formState.errors?.translations &&
                                  (formState.errors?.translations[index]
                                    ?.language?.message as string)
                                }
                                isInvalid={Boolean(
                                  formState.errors?.translations &&
                                    formState.errors?.translations[index]
                                      ?.language
                                )}
                                wrapperClass="col-lg-6"
                                label={t(
                                  "createQuestion.translations.languageTranslation.label"
                                )}
                                defaultValue={item.language}
                                placeholder={t(
                                  "createQuestion.translations.languageTranslation.placeholder"
                                )}
                                {...register(`translations.${index}.language`, {
                                  required: {
                                    value: true,
                                    message: t("form-generic.error.required"),
                                  },
                                })}
                                onChange={(selected) => {
                                  clearErrors(`translations.${index}.language`);
                                  setSelectedTranslations((old) => {
                                    if (old.includes(selected)) return [...old];
                                    else return [...old!, selected];
                                  });
                                  setValue(
                                    `translations.${index}.language`,
                                    selected as LANGUAGE_TRANSLATION
                                  );
                                }}
                              />
                              {index ? (
                                <i
                                  onClick={() => removeLanguage(index)}
                                  className="icon-delete-translations icon-delete pointer"
                                />
                              ) : null}
                            </>
                          )}
                        </div>
                        <div>
                          {isLoading ? (
                            <Skeleton
                              height={176}
                              style={{ marginBottom: "24px" }}
                            />
                          ) : (
                            <CustomTextArea
                              wrapperClass="col-lg-12"
                              rows={4}
                              register={register(`translations.${index}.text`, {
                                required: {
                                  value: true,
                                  message: t("form-generic.error.required"),
                                },
                              })}
                              label={t(
                                "createQuestion.questionData.fields.question.label"
                              )}
                              required
                              placeholder={t(
                                "createQuestion.questionData.fields.question.placeholder"
                              )}
                              typeErrors={
                                formState.errors?.translations &&
                                (formState.errors?.translations[index]?.text
                                  ?.message as string)
                              }
                              isInvalid={Boolean(
                                formState.errors?.translations &&
                                  formState.errors?.translations[index]?.text
                              )}
                              defaultValue={item.text}
                            />
                          )}
                        </div>
                        {!index && (
                          <>
                            <div className="mb-4">
                              <h2 className="registry">
                                {t("createQuestion.translations.title")}
                              </h2>
                              <p className="page-description">
                                {t("createQuestion.translations.text")}
                              </p>
                            </div>
                          </>
                        )}
                      </Fragment>
                    ))} */}
                </div>
              </div>
              {/*  <div className="row single-section">
                <Button
                  fullWidth
                  outline
                  onClick={() => handleAddLanguage()}
                  disabled={question && question.translations?.length > 5}
                  className="capitalize-first-letter "
                >
                  <span>{t("createQuestion.translations.buttonAdd")}</span>
                  <span className="plus-char">+</span>
                </Button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {isInModal && (
        <div className="footer ">
          <Button onClick={() => setOpenModal && setOpenModal(false)} outline>
            {t("home.companies.modal.cancel")}
          </Button>
          <Button btnType="submit" onClick={handleSubmit(onSumbitForm)}>
            {t("home.companies.modal.confirm")}
          </Button>
        </div>
      )}
    </form>
  );
};

export default FormCreateQuestion;
